import { useEffect, useState } from 'react';
import { Card, Image, Text, Badge, Group, Button, Box, Title, ActionIcon, Skeleton } from '@mantine/core';
import { IconCalendar, IconMapPin, IconClock, IconTicket, IconBookmark } from '@tabler/icons-react';

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  event_type_ids: number[]; // Array of event type IDs
  event_types: { id: number; name: string }[]; // Array of event type objects
  venue_name: string;
  address: string;
  city: string;
  state: string;
  country?: string;
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  event_type?: string;
  dance_style?: string;
  [key: string]: any; // Allows additional fields
}

interface EventCardProps {
  event: Event;
  onCardClick?: (event: Event) => void; // Add this prop for drawer functionality
}

export default function EventCard({ event, onCardClick }: EventCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {}, [event]);

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format full date for display
  const formatFullDate = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };

    // Check if the dates are the same (comparing year, month, and day)
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    ) {
      // If same date, return only the start date in long format
      return start.toLocaleDateString('en-US', options);
    }

    // Otherwise, return the range format as before
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', { day: 'numeric' })}`;
  };

  const startDate = formatDate(event.event_start_date);
  const startTime = formatTime(event.event_start_date);

  // Get country flag emoji
  const getCountryFlag = (country?: string) => {
    // Default to US flag if no country provided
    if (!country || country.toLowerCase() === 'united states' || country.toLowerCase() === 'usa') {
      return '🇺🇸';
    }
    // Add more country flags as needed
    return '';
  };

  // Determine if the event is in the past
  const isPastEvent = new Date(event.event_end_date) < new Date();

  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    // If clicking on a link or button inside the card, don't trigger the drawer
    if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) {
      return;
    }

    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();

    // Call the parent's onCardClick if provided
    if (onCardClick) {
      onCardClick(event);
    }
  };

  return (
    <Card
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#fff7ed1a',
        border: '1px solid #f2f2f230',
        cursor: onCardClick ? 'pointer' : 'default', // Change cursor to pointer if clickable
      }}
      onClick={handleCardClick} // Add onClick handler
    >
      {/* Image Section with Event Type Badge */}
      <Card.Section style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
        {!imageLoaded && <Skeleton height={200} radius="md" animate={true} />}
        {/* Event Image */}
        <Image
          src={event.banner_url || 'https://placehold.co/600x400?text=No+Image'}
          alt={event.name}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* Event Type Badges - Upper Left Corner */}
        {event.event_types && event.event_types.length > 0 && (
          <Group
            style={{
              position: 'absolute',
              top: 10, // Adjust spacing from the top
              left: 10, // Adjust spacing from the left
              zIndex: 10, // Ensure it appears above the image
              gap: '0.3rem',
            }}
          >
            {event.event_types.map((type) => (
              <Badge
                key={type.id}
                color="gray"
                variant="filled"
                className="poppins"
                style={{ color: '#FFF7ED', background: '#75757590' }}
              >
                {type.name}
              </Badge>
            ))}
          </Group>
        )}

        {/* Bookmark Icon - Lower Right Corner */}
        <ActionIcon
          variant="transparent"
          color="white"
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            width: 35,
            height: 35,
            zIndex: 10,
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click when clicking bookmark
            // Add bookmark functionality here
          }}
        >
          <IconBookmark size={28} />
        </ActionIcon>
      </Card.Section>

      {/* Info Section */}
      <Box
        px="lg"
        style={{
          backgroundColor: 'rgba(28, 28, 28, 0.95)',
          color: 'white',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Group
          style={{
            gap: '0.3rem',
          }}
        >
          <IconCalendar size={20} style={{ color: '#FFF7ED' }} />
          <Text size="sm" c="gray.4" className="poppins">
            {formatFullDate(event.event_start_date, event.event_end_date)}
          </Text>
        </Group>

        <Title order={4} lineClamp={2} style={{ color: '#FFF7ED' }} className="poppins">
          {event.name}
        </Title>

        <Group>
          <IconMapPin size={16} style={{ color: '#FFF7ED', marginRight: 0 }} />
          <Text size="sm" lineClamp={1} style={{ color: '#FFF7ED' }} className="poppins">
            {event.city && ` ${event.city}`}
            {event.state && `, ${event.state}`}
            {event.country && ` ${getCountryFlag(event.country)}`}
          </Text>
        </Group>

        {event.dance_style && (
          <Group>
            <IconMapPin size={16} style={{ color: '#FFF7ED' }} />
            <Text size="sm" style={{ color: '#FFF7ED' }} className="poppins">
              Style: {event.dance_style}
            </Text>
          </Group>
        )}

        {/* View Details Button - This will still work to navigate directly to the event page 
        <Button
          component="a"
          href={`/events/${event.slug}`}
          variant="outline"
          color="#EA580C"
          size="xs"
          mt="auto"
          mb="md"
          style={{ alignSelf: 'flex-end' }}
          onClick={(e) => e.stopPropagation()} // Prevent drawer from opening when clicking button
        >
          Full Details
        </Button> */}
      </Box>
    </Card>
  );
}
